/**
 * utils/SmartPmBackendService.js
 * Service de base pour toute communication avec le backend SMART-PM
 */

import logger from "./logger.js";

class SmartPmBackendService {
  constructor() {
    const isDev =
      typeof window !== "undefined" &&
      (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");

    this.baseUrl = isDev ? "http://127.0.0.1:5000" : "/smart-pm/api";
    this.isServiceLive = false;
    this.lastHealthCheck = null;
    this.healthCheckCache = null;
    this.healthCheckCacheDuration = 30000; // 30 secondes de cache
  }

  /**
   * Health check avec cache pour éviter les appels redondants
   * @param {boolean} [forceRefresh=false] - Forcer un nouveau check même si le cache est valide
   * @returns {Promise<Object>} {healthy: boolean, data?: Object, error?: string}
   */
  async checkHealth(forceRefresh = false) {
    const now = Date.now();

    // Utiliser le cache si disponible et valide
    if (
      !forceRefresh &&
      this.healthCheckCache &&
      now - this.healthCheckCache.timestamp < this.healthCheckCacheDuration
    ) {
      logger.info(
        {
          context: "health-check-cache",
          cacheAge: now - this.healthCheckCache.timestamp,
        },
        "Utilisation du cache health check"
      );
      return this.healthCheckCache.result;
    }

    try {
      const response = await fetch(`${this.baseUrl}/health`, {
        method: "GET",
        headers: {
          Accept: "application/json",
        },
        signal: AbortSignal.timeout(3000),
      });

      if (response.ok) {
        const healthData = await response.json();
        this.isServiceLive = true;
        this.lastHealthCheck = new Date();

        const result = { healthy: true, data: healthData };
        this.healthCheckCache = { result, timestamp: now };

        logger.info(
          {
            context: "backend-health-check",
            status: "healthy",
            serviceUrl: this.baseUrl,
          },
          "Backend SMART-PM disponible"
        );

        return result;
      } else {
        throw new Error(`Service responded with status ${response.status}`);
      }
    } catch (error) {
      this.isServiceLive = false;
      this.lastHealthCheck = new Date();

      const result = { healthy: false, error: error.message };
      this.healthCheckCache = { result, timestamp: now };

      logger.warn(
        {
          context: "backend-health-check",
          status: "unhealthy",
          serviceUrl: this.baseUrl,
          error: error.message,
        },
        "Backend SMART-PM indisponible"
      );

      return result;
    }
  }

  /**
   * Requête générique avec gestion d'erreur unifiée
   * @param {string} endpoint - Endpoint de l'API (ex: "/enrich-excel")
   * @param {Object} [options={}] - Options fetch (method, headers, body, etc.)
   * @returns {Promise<Object>} {success: boolean, response: Response|null, status: number}
   */
  async request(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;

    try {
      const response = await fetch(url, {
        ...options,
        headers: {
          Accept: "application/json",
          ...options.headers,
        },
      });

      logger.info(
        {
          context: "backend-request",
          endpoint,
          method: options.method || "GET",
          status: response.status,
          ok: response.ok,
        },
        "Requête backend exécutée"
      );

      return { success: response.ok, response, status: response.status };
    } catch (error) {
      logger.error(
        {
          context: "backend-request-error",
          endpoint,
          method: options.method || "GET",
          error: error.message,
          stack: error.stack,
        },
        "Erreur requête backend"
      );

      return { success: false, error: error.message, response: null, status: 0 };
    }
  }

  /**
   * Obtenir le statut actuel du service
   * @returns {Object} Statut détaillé du backend
   */
  getServiceStatus() {
    return {
      isLive: this.isServiceLive,
      lastCheck: this.lastHealthCheck,
      url: this.baseUrl,
      cacheValid: this.healthCheckCache && Date.now() - this.healthCheckCache.timestamp < this.healthCheckCacheDuration,
      cacheAge: this.healthCheckCache ? Date.now() - this.healthCheckCache.timestamp : null,
    };
  }

  /**
   * Formater la taille de fichier
   * @param {number} bytes - Taille en bytes
   * @returns {string} Taille formatée (B, KB, MB)
   */
  formatFileSize(bytes) {
    if (bytes < 1024) {
      return `${bytes} B`;
    } else if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    } else {
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }
  }

  /**
   * Invalider le cache du health check
   */
  invalidateHealthCache() {
    this.healthCheckCache = null;
    logger.info(
      {
        context: "health-cache-invalidation",
      },
      "Cache health check invalidé"
    );
  }
}

// Instance singleton partagée par tous les services
export const smartPmBackend = new SmartPmBackendService();
